import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Printer } from 'lucide-react';

/**
 * PrintBinLabel Component V4
 * Optimized - fills gap by increasing content sizes, footer stays same
 */
const PrintBinLabel = ({ location, onClose }) => {
  const labelRef = useRef(null);
  
  const qrData = JSON.stringify({
    bln: location.bin_code,
    loc: location.location_code,
    site: location.site,
    zone: location.zone,
    rack: location.aisle,
    shelve: location.rack,
    level: location.level,
    path: location.display_path
  });
  
  const formatDimensions = () => {
    const dims = [];
    if (location.bin_length_mm) dims.push(`${location.bin_length_mm}L`);
    if (location.bin_width_mm) dims.push(`${location.bin_width_mm}W`);
    if (location.bin_height_mm) dims.push(`${location.bin_height_mm}H`);
    return dims.length > 0 ? dims.join('×') + 'mm' : null;
  };
  
  const handlePrint = () => {
    const printContent = labelRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>BIN ${location.bin_code} - ${location.location_code}</title>
          <style>
            @page { size: 100mm 60mm; margin: 0; }
            body { margin: 0; padding: 0; font-family: 'Arial', sans-serif; }
            * { box-sizing: border-box; }
            .label-container {
              width: 100mm;
              height: 60mm;
              padding: 2.5mm;
              border: 0.4mm solid #000;
              display: flex;
              flex-direction: column;
            }
            .top-section {
              display: flex;
              gap: 3mm;
              flex: 0 0 auto;
            }
            .qr-box {
              flex-shrink: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .qr-text {
              font-size: 5pt;
              color: #666;
              margin-top: 0.8mm;
              text-transform: uppercase;
            }
            .codes-box {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 2mm;
            }
            .bin-hero {
              background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
              color: white;
              padding: 2.5mm 3mm;
              border-radius: 1.5mm;
              text-align: center;
            }
            .bin-title {
              font-size: 7pt;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 2px;
              opacity: 0.9;
            }
            .bin-code {
              font-size: 26pt;
              font-weight: 900;
              font-family: 'Courier New', monospace;
              letter-spacing: 4px;
              line-height: 1;
              margin-top: 0.5mm;
            }
            .loc-row {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 2mm;
              background: #f1f5f9;
              padding: 2mm;
              border-radius: 1mm;
              border: 0.2mm solid #cbd5e1;
            }
            .loc-title {
              font-size: 7pt;
              color: #64748b;
              text-transform: uppercase;
              font-weight: 500;
            }
            .loc-code {
              font-size: 15pt;
              font-weight: bold;
              font-family: 'Courier New', monospace;
              color: #334155;
              letter-spacing: 2px;
            }
            .hierarchy-row {
              display: flex;
              gap: 1.2mm;
              margin-top: 2mm;
            }
            .h-box {
              flex: 1;
              background: #1e293b;
              color: white;
              padding: 1.5mm 0.5mm;
              text-align: center;
              border-radius: 1mm;
            }
            .h-label {
              font-size: 5pt;
              opacity: 0.7;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            .h-value {
              font-size: 9pt;
              font-weight: bold;
              margin-top: 0.5mm;
            }
            .desc-row {
              margin-top: 2mm;
              padding: 1.5mm 2mm;
              background: #eff6ff;
              border: 0.2mm solid #bfdbfe;
              border-radius: 1mm;
              text-align: center;
            }
            .desc-text {
              font-size: 8pt;
              color: #1e40af;
              font-weight: 600;
              margin: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .footer-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: auto;
              padding-top: 1mm;
              border-top: 0.2mm solid #e2e8f0;
            }
            .meta-left {
              display: flex;
              align-items: center;
              gap: 1.5mm;
            }
            .dims {
              font-size: 6pt;
              color: #64748b;
              font-family: 'Courier New', monospace;
            }
            .color-tag {
              padding: 0 1mm;
              border-radius: 0.6mm;
              font-size: 5pt;
              font-weight: bold;
              margin-bottom: 0.5mm;
            }
            .brand {
              font-size: 5pt;
              color: #94a3b8;
              font-weight: 600;
              letter-spacing: 0.8px;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  
  const dimensions = formatDimensions();
  
  const getColorStyle = (color) => {
    if (!color) return { backgroundColor: '#f3f4f6', color: '#374151' };
    const c = color.toLowerCase();
    const styles = {
      blue: { backgroundColor: '#3b82f6', color: '#ffffff' },
      yellow: { backgroundColor: '#eab308', color: '#000000' },
      red: { backgroundColor: '#ef4444', color: '#ffffff' },
      green: { backgroundColor: '#22c55e', color: '#ffffff' },
      orange: { backgroundColor: '#f97316', color: '#ffffff' },
      white: { backgroundColor: '#ffffff', color: '#000000', border: '1px solid #000' },
      black: { backgroundColor: '#000000', color: '#ffffff' },
      grey: { backgroundColor: '#6b7280', color: '#ffffff' },
    };
    return styles[c] || { backgroundColor: '#f3f4f6', color: '#374151' };
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Print Bin Label</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Label Preview */}
        <div className="p-6">
          <p className="text-sm text-slate-500 mb-4">Label Preview (100mm × 60mm)</p>
          
          <div 
            ref={labelRef}
            className="border-2 border-slate-800 bg-white mx-auto"
            style={{ width: '378px', height: '227px' }}
          >
            <div className="label-container p-2.5 h-full flex flex-col">
              {/* Top Section: QR + Codes */}
              <div className="top-section flex gap-3">
                {/* QR Code - slightly larger */}
                <div className="qr-box flex-shrink-0 flex flex-col items-center">
                  <QRCodeSVG value={qrData} size={78} level="M" includeMargin={false} />
                  <span className="qr-text text-[7px] text-slate-400 mt-1 uppercase tracking-wide">Scan for details</span>
                </div>
                
                {/* Codes - increased sizes */}
                <div className="codes-box flex-1 flex flex-col gap-2">
                  {/* BIN Hero - larger */}
                  <div className="bin-hero bg-gradient-to-br from-amber-500 to-amber-600 text-white py-2 px-3 rounded-lg text-center">
                    <div className="bin-title text-[9px] font-semibold uppercase tracking-[2px] opacity-90">BIN</div>
                    <div className="bin-code text-[28px] font-black font-mono tracking-[5px] leading-none">
                      {location.bin_code}
                    </div>
                  </div>
                  
                  {/* Location Code - larger */}
                  <div className="loc-row flex items-center justify-center gap-2 bg-slate-100 py-1.5 px-2 rounded border border-slate-300">
                    <span className="loc-title text-[8px] text-slate-500 uppercase font-medium">Location</span>
                    <span className="loc-code text-lg font-bold font-mono text-slate-700 tracking-[2px]">
                      {location.location_code || '------'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Hierarchy Boxes - larger */}
              <div className="hierarchy-row flex gap-1 mt-2">
                {[
                  { label: 'SITE', value: location.site },
                  { label: 'ZONE', value: location.zone },
                  { label: 'RACK #', value: location.aisle },
                  { label: 'SHELVE #', value: location.rack },
                  { label: 'LEVEL', value: location.level },
                ].map((item, idx) => (
                  <div key={idx} className="h-box flex-1 bg-slate-800 text-white py-1.5 px-0.5 text-center rounded">
                    <div className="h-label text-[7px] opacity-70 tracking-wide">{item.label}</div>
                    <div className="h-value text-sm font-bold">{item.value || '-'}</div>
                  </div>
                ))}
              </div>
              
              {/* Description - larger */}
              {location.description && (
                <div className="desc-row mt-2 py-1.5 px-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="desc-text text-[11px] text-blue-800 font-semibold text-center truncate">
                    {location.description}
                  </p>
                </div>
              )}
              
              {/* Footer - stays same size */}
              <div className="footer-row flex justify-between items-center mt-auto pt-1 border-t border-slate-200">
                <div className="meta-left flex items-center gap-1.5">
                  {dimensions && (
                    <span className="dims text-[8px] text-slate-500 font-mono">{dimensions}</span>
                  )}
                  {location.bin_color && (
                    <span 
                      className="color-tag px-1 py-0 rounded text-[6px] font-bold"
                      style={{...getColorStyle(location.bin_color), marginBottom: '2px'}}
                    >
                      {location.bin_color}
                    </span>
                  )}
                </div>
                <div className="brand text-[8px] text-slate-400 font-semibold tracking-wider">
                  HADEN ATLAS
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end space-x-3 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800">
            Cancel
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Printer className="w-4 h-4" />
            <span>Print Label</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintBinLabel;
